import { useQuery, useQueryClient } from '@tanstack/react-query';

import { addonDetail } from '@api/sdk.gen';
import {
  AddonHealthStatus,
  AddonLifecycleStatus,
  AddonsAddonListItem,
  AddonsAddonListResponse,
} from '@api/types.gen';

import { withError } from '@/react-tools/react-query';

import { queryKeys } from '../query-keys';

const REFETCH_INTERVAL_WHILE_IN_PROGRESS = 5000;

// Slower than an operation in flight: a credential fault is a steady state that
// may never clear, and every poll costs Portainer a live probe of the add-on.
const REFETCH_INTERVAL_WHILE_CREDENTIAL_INVALID = 15000;

export function useAddon(id: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.detail(id),
    queryFn: () => getAddon(id),
    enabled: !!id,
    // The list holds the same shape, so seed the first paint from it (if the
    // user came from the list) while the detail refetches in the background.
    placeholderData: () =>
      queryClient
        .getQueryData<AddonsAddonListResponse>(queryKeys.list())
        ?.addons?.find((addon) => addon.id === id),
    refetchInterval: (data) => pollInterval(data),
    ...withError('Unable to load add-on'),
  });
}

/**
 * How often to keep asking, when the add-on is expected to change state without
 * the user doing anything.
 *
 * A repair leaves the add-on Installed throughout, so watching the lifecycle
 * alone would freeze the badge on the state the user just acted to clear.
 * Kubernetes takes up to a minute to propagate the new credential, well past the
 * refetch that follows the request.
 */
export function pollInterval(data?: AddonsAddonListItem) {
  if (isAddonInProgress(data)) {
    return REFETCH_INTERVAL_WHILE_IN_PROGRESS;
  }

  if (
    data?.healthStatus === AddonHealthStatus.ADDON_HEALTH_CREDENTIAL_INVALID
  ) {
    return REFETCH_INTERVAL_WHILE_CREDENTIAL_INVALID;
  }

  return false as const;
}

function isAddonInProgress(data?: AddonsAddonListItem) {
  return (
    data?.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_INSTALLING ||
    data?.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_UNINSTALLING ||
    data?.lifecycleStatus === AddonLifecycleStatus.ADDON_STATUS_UPGRADING
  );
}

async function getAddon(id: string) {
  const { data } = await addonDetail({ path: { id } });
  return data;
}
